import { snackMenuItemsOptions, updateSnackMenuItem, useBuilding } from "@/api";
import PageBackButton from "@/components/PageBackButton";
import { useBuildingQuery } from "@/components/RouterSetup";
import MenuItemForm from "@/components/snack/MenuItemForm";
import { useWelcomeSnackAccess } from "@/hooks/useWelcomeSnackAccess";
import { getBuildingId, notifyApiError } from "@/lib";
import { useRouterStore, useSnackbarStore } from "@/store/zustandStore";
import { NotificationType, SnackMenuItem } from "@/types";
import { Breadcrumbs, LoadingSpinner } from "@app-components";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_portal/snack-admin/menu/item/$itemId")({
  component: RouteComponent,
  loader: () => {
    const { queryClient, supplierCompanyId } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.ensureQueryData(snackMenuItemsOptions(supplierCompanyId, true));
  },
});

function RouteComponent() {
  const { t, i18n } = useTranslation();
  const { allowSnackAdminAccess } = useWelcomeSnackAccess();
  const navigate = useNavigate();
  const params = Route.useParams();
  const queryClient = useQueryClient();
  const { addNotification } = useSnackbarStore();
  const { data: building } = useBuilding();
  const { data: menu, isLoading } = useBuildingQuery(
    snackMenuItemsOptions(building.supplierCompanyId, true),
  );
  const item = useMemo(
    () => menu?.items.find((i) => i.id === params.itemId),
    [menu, params.itemId],
  );

  const breadcrumbs = useMemo(
    () => [
      { label: t("SIDEBAR.SNACK_MENU"), path: "/snack-admin/menu" },
      {
        label: item?.name[i18n.language === "en" ? "en" : "no"] ?? "",
        path: `/snack-admin/menu/item/${params.itemId}`,
      },
    ],
    [item],
  );

  const handleSubmit = async (
    values: Partial<SnackMenuItem>,
    onSuccess: () => void,
  ) => {
    const entity = (values.name ?? item?.name)?.[
      i18n.language === "en" ? "en" : "no"
    ];
    try {
      await updateSnackMenuItem(params.itemId, values);

      addNotification(
        t("NOTIFICATIONS.DOCUMENT_UPDATED", {
          entity,
        }),
        NotificationType.SUCCESS,
      );

      queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "menu-items", building.supplierCompanyId],
      });

      onSuccess();
    } catch (error) {
      console.error(error);
      notifyApiError(error, addNotification, t, {
        properties: {
          entity,
        },
        fallbackKey: "UPDATE_FAILED",
      });
    }
  };

  useEffect(() => {
    if (!isLoading && !item) {
      navigate({ to: "/404", replace: true });
    }
  }, [isLoading, item]);

  if (!allowSnackAdminAccess) return null;
  return (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton fallbackPath={{ to: breadcrumbs[0].path }} />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      {isLoading ? (
        <div className="center">
          <LoadingSpinner />
        </div>
      ) : (
        item && <MenuItemForm item={item} handleSubmit={handleSubmit} />
      )}
    </>
  );
}
